import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const POST_MODES = [
  { v: 'dry_run', label: 'Dry run (save findings only — never touch the PR)' },
  { v: 'summary_only', label: 'Summary comment only' },
  { v: 'inline', label: 'Inline comments + summary' },
];
const SEVERITIES = ['info', 'warning', 'error'];
const PROVIDERS = ['bitbucket', 'gitea', 'github', 'gitlab'];
// Providers that can answer "which repos have an open PR?" instance-wide. Bitbucket
// Cloud has no workspace-wide open-PR search, so it can only be scanned repo by repo.
const SEARCHABLE_PROVIDERS = ['gitea', 'github', 'gitlab'];
// Only a seed for the first render — /code-review/models is the source of truth
// for both the list and the default, so a new model needs no UI change.
const FALLBACK_MODEL = 'claude-opus-5';
const REPO_PAGE = 50;
// /repositories caps a page at 200, so select-all walks pages at that size.
const SELECT_ALL_PAGE = 200;
// Above this, select-all asks first — every selected repo is scanned each run.
const SELECT_ALL_CONFIRM_AT = 500;
const MAX_CHIPS = 25;
// Selected repos are pinned above the search results. Beyond this many, the rest
// are reachable by searching — rendering thousands of rows would stall the form.
const MAX_PINNED = 50;

function toLines(arr) { return (arr || []).join('\n'); }
function fromLines(s) {
  return (s || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean);
}

export default function ReviewConfigForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;

  const [repos, setRepos] = useState([]);
  const [repoTotal, setRepoTotal] = useState(0);
  const [repoPage, setRepoPage] = useState(1);
  const [repoPages, setRepoPages] = useState(1);
  const [reposLoading, setReposLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  // id -> repo row, so chips and the Gitea warning survive a changing result set.
  const [selectedRepos, setSelectedRepos] = useState({});
  const [packs, setPacks] = useState([]);
  const [models, setModels] = useState([]);
  const [modelsLive, setModelsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [repoSearch, setRepoSearch] = useState('');
  const [repoProvider, setRepoProvider] = useState('');

  const [f, setF] = useState({
    name: '', repo_scope: 'selected', scope_provider: '', scope_owner: '',
    repository_ids: [], target_branches: 'main',
    cron_expression: '0 3 * * *', include_globs: '', exclude_globs: '', exclude_title_regex: '',
    standard_packs: ['security', 'performance', 'error_handling', 'exotel_custom'],
    max_prs_per_run: 0, max_comments_per_pr: 25, max_files_per_pr: 40, min_severity: 'info',
    min_confidence: 0.3, skip_drafts: true, post_mode: 'dry_run',
    llm_model: FALLBACK_MODEL, enabled: true,
  });

  // The picker searches server-side: `repositories` runs into the thousands and
  // /repositories caps a page at 200, so a single fetch only ever returned the
  // alphabetically first slice of the alphabetically first provider.
  const fetchRepos = useCallback(async (q, provider, page) => {
    setReposLoading(true);
    try {
      const params = { page, limit: REPO_PAGE };
      if (q)        params.q        = q;
      if (provider) params.provider = provider;
      const res = await axios.get('/repositories', { params });
      setRepos(res.data.data || []);
      setRepoTotal(res.data.total || 0);
      setRepoPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load repositories');
    } finally {
      setReposLoading(false);
    }
  }, []);

  // A narrower search invalidates whatever page we were on. This runs before the
  // fetch effect below, whose stale timeout is cleared by the resulting re-render.
  useEffect(() => { setRepoPage(1); }, [repoSearch, repoProvider]);

  useEffect(() => {
    const t = setTimeout(() => fetchRepos(repoSearch.trim(), repoProvider, repoPage), 250);
    return () => clearTimeout(t);
  }, [repoSearch, repoProvider, repoPage, fetchRepos]);

  useEffect(() => {
    Promise.all([
      axios.get('/code-review/packs'),
      editing ? axios.get(`/code-review/configs/${id}`) : Promise.resolve(null),
      axios.get('/code-review/models').catch(() => null),
    ]).then(([p, c, m]) => {
      setPacks(p.data.data || []);
      if (m) {
        setModels(m.data.models || []);
        setModelsLive(!!m.data.live_checked);
        // A new config starts on whatever the server calls current, so the
        // default follows model releases without a UI change.
        if (!editing && m.data.default) setF(prev => ({ ...prev, llm_model: m.data.default }));
      }
      if (c) {
        const d = c.data;
        // Seed the lookup from the config itself — the repos it already uses
        // need not show up in the current search results.
        setSelectedRepos(Object.fromEntries((d.repositories || []).map(r => [r.id, r])));
        setF({
          name: d.name || '',
          repo_scope: d.repo_scope === 'all' ? 'all' : 'selected',
          scope_provider: d.scope_provider || '',
          scope_owner: d.scope_owner || '',
          repository_ids: d.repository_ids || (d.repository_id ? [d.repository_id] : []),
          target_branches: toLines(d.target_branches),
          cron_expression: d.cron_expression || '0 3 * * *',
          include_globs: toLines(d.include_globs), exclude_globs: toLines(d.exclude_globs),
          exclude_title_regex: d.exclude_title_regex || '',
          standard_packs: d.standard_packs || [],
          max_prs_per_run: d.max_prs_per_run ?? 0, max_comments_per_pr: d.max_comments_per_pr ?? 25,
          max_files_per_pr: d.max_files_per_pr ?? 40,
          min_severity: d.min_severity || 'info', min_confidence: d.min_confidence ?? 0.3,
          skip_drafts: !!d.skip_drafts, post_mode: d.post_mode || 'dry_run',
          llm_model: d.llm_model || FALLBACK_MODEL, enabled: !!d.enabled,
        });
      }
    }).catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, editing]);

  // Gitea can't take inline comments before 1.12, so the post-mode field warns when
  // one of the selected repos lives there.
  const hasGiteaRepo = f.repo_scope === 'all'
    ? f.scope_provider === 'gitea'
    : f.repository_ids.some(rid => selectedRepos[rid]?.provider === 'gitea');

  // A saved config may name a model the server no longer lists. Keep it as an
  // option so opening the form never silently reassigns someone's config.
  const modelOptions = models.some(m => m.id === f.llm_model) || !f.llm_model
    ? models
    : [{ id: f.llm_model, label: f.llm_model, available: null }, ...models];
  const selectedModel = modelOptions.find(m => m.id === f.llm_model);

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })); }
  function toggleRepo(repo) {
    setSelectedRepos(prev => ({ ...prev, [repo.id]: repo }));
    setF(prev => ({ ...prev, repository_ids: prev.repository_ids.includes(repo.id)
      ? prev.repository_ids.filter(x => x !== repo.id) : [...prev.repository_ids, repo.id] }));
  }
  function removeRepo(rid) {
    setF(prev => ({ ...prev, repository_ids: prev.repository_ids.filter(x => x !== rid) }));
  }
  function selectRepos(rows) {
    setSelectedRepos(prev => ({ ...prev, ...Object.fromEntries(rows.map(r => [r.id, r])) }));
    setF(prev => ({ ...prev,
      repository_ids: [...new Set([...prev.repository_ids, ...rows.map(r => r.id)])] }));
  }
  function clearSelection() {
    setF(prev => ({ ...prev, repository_ids: [] }));
  }

  // Already-selected repos ride at the top of the picker, so opening a config
  // shows what it covers instead of hiding it somewhere in 40 pages of results.
  // The search box filters them client-side — the server page they live on is
  // irrelevant once they're pinned.
  const pinnedRepos = useMemo(() => {
    const q = repoSearch.trim().toLowerCase();
    return f.repository_ids
      .map(rid => selectedRepos[rid] || { id: rid, repo_name: `#${rid}`, provider: '?' })
      .filter(r => (!repoProvider || r.provider === repoProvider)
        && (!q || `${r.provider}:${r.repo_name}`.toLowerCase().includes(q)))
      .sort((a, b) => (a.provider || '').localeCompare(b.provider || '')
        || (a.repo_name || '').localeCompare(b.repo_name || ''));
  }, [f.repository_ids, selectedRepos, repoSearch, repoProvider]);

  const pinnedShown = pinnedRepos.slice(0, MAX_PINNED);
  const pinnedIds = new Set(pinnedShown.map(r => r.id));
  // Don't draw a repo twice when its server page happens to be the one on screen.
  const pageRepos = repos.filter(r => !pinnedIds.has(r.id));

  // Selects every repo matching the current search/provider, not just the page
  // on screen — walks the pages server-side and unions the ids in.
  async function selectAllMatching() {
    if (repoTotal > SELECT_ALL_CONFIRM_AT && !window.confirm(
      `Select all ${repoTotal.toLocaleString()} matching repositories? `
      + 'Every one of them is scanned on each run.')) return;
    setSelectingAll(true);
    setError('');
    try {
      const base = { limit: SELECT_ALL_PAGE };
      if (repoSearch.trim()) base.q        = repoSearch.trim();
      if (repoProvider)      base.provider = repoProvider;
      const rows = [];
      let page = 1, pages = 1;
      do {
        const res = await axios.get('/repositories', { params: { ...base, page } });
        rows.push(...(res.data.data || []));
        pages = res.data.pages || 1;
        page += 1;
      } while (page <= pages);
      selectRepos(rows);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to select all repositories');
    } finally {
      setSelectingAll(false);
    }
  }
  function togglePack(key) {
    setF(prev => ({ ...prev, standard_packs: prev.standard_packs.includes(key)
      ? prev.standard_packs.filter(x => x !== key) : [...prev.standard_packs, key] }));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!f.name.trim()) return setError('Name is required');
    const allScope = f.repo_scope === 'all';
    if (allScope) {
      if (!f.scope_provider) return setError('Pick the provider to search for open PRs');
    } else if (!f.repository_ids || f.repository_ids.length === 0) {
      return setError('Pick at least one repository');
    }
    const branches = fromLines(f.target_branches);
    if (branches.length === 0) return setError('At least one target branch is required');

    const titleRegex = f.exclude_title_regex.trim();
    if (titleRegex) {
      try { new RegExp(titleRegex); }
      catch (re) { return setError(`Exclude-title regex is invalid: ${re.message}`); }
    }

    const payload = {
      name: f.name.trim(),
      // The two modes are mutually exclusive server-side, so send exactly one of
      // them populated: a leftover repo list with repo_scope 'all' is rejected.
      repo_scope: f.repo_scope,
      scope_provider: allScope ? f.scope_provider : null,
      scope_owner: allScope ? (f.scope_owner.trim() || null) : null,
      repository_ids: allScope ? [] : f.repository_ids.map(Number),
      target_branches: branches, cron_expression: f.cron_expression.trim(),
      include_globs: fromLines(f.include_globs), exclude_globs: fromLines(f.exclude_globs),
      exclude_title_regex: titleRegex || null,
      standard_packs: f.standard_packs,
      max_prs_per_run: Number(f.max_prs_per_run), max_comments_per_pr: Number(f.max_comments_per_pr),
      max_files_per_pr: Number(f.max_files_per_pr),
      min_severity: f.min_severity, min_confidence: Number(f.min_confidence),
      skip_drafts: f.skip_drafts, post_mode: f.post_mode, llm_model: f.llm_model.trim(),
      enabled: f.enabled,
    };
    setSaving(true);
    try {
      if (editing) await axios.put(`/code-review/configs/${id}`, payload);
      else await axios.post('/code-review/configs', payload);
      navigate('/code-review');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      setSaving(false);
    }
  }

  if (loading) return <div className="page-content"><div className="spinner" /></div>;

  return (
    <div className="page-content" style={{ maxWidth: 760 }}>
      <div className="page-header">
        <h2 className="page-title">{editing ? 'Edit' : 'New'} review config</h2>
        <p className="page-subtitle">Choose a repository and branches, set rules and limits, and pick how findings are delivered.</p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <form onSubmit={submit}>
        <section className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-control" value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Telephony · main + releases" />
          </div>
          <div className="form-group">
            <label className="form-label">
              Repositories{f.repo_scope === 'selected' ? ` (${f.repository_ids.length} selected)` : ''}
            </label>
            <div style={{ display: 'flex', gap: 18, margin: '2px 0 10px', fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="repo_scope" checked={f.repo_scope === 'selected'}
                  onChange={() => set('repo_scope', 'selected')} />
                Pick repositories
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="repo_scope" checked={f.repo_scope === 'all'}
                  onChange={() => set('repo_scope', 'all')} />
                Every repo with an open PR
              </label>
            </div>
            {f.repo_scope === 'all' ? (<>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="form-control" style={{ maxWidth: 200 }} value={f.scope_provider}
                onChange={e => set('scope_provider', e.target.value)}>
                <option value="">Choose a provider…</option>
                {SEARCHABLE_PROVIDERS.map(prov => <option key={prov} value={prov}>{prov}</option>)}
              </select>
              <input className="form-control" placeholder="Owner, org or group (optional)"
                value={f.scope_owner} onChange={e => set('scope_owner', e.target.value)} />
            </div>
            <p className="form-hint">
              Each run asks {f.scope_provider || 'the provider'} which repositories currently have an
              open pull request, then reviews only those against the target branches below. Nothing to
              maintain as repos come and go, and repos with no open PR cost nothing. Leave the owner
              blank to cover everything the provider token can see. Bitbucket is not offered here: it
              has no workspace-wide open-PR search, so a Bitbucket config has to name its repositories.
            </p>
            </>) : (<>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="form-control" placeholder="Search repositories…" value={repoSearch}
                onChange={e => setRepoSearch(e.target.value)} />
              <select className="form-control" style={{ maxWidth: 160 }} value={repoProvider}
                onChange={e => setRepoProvider(e.target.value)}>
                <option value="">All providers</option>
                {PROVIDERS.map(prov => <option key={prov} value={prov}>{prov}</option>)}
              </select>
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 8 }}>
              {pinnedShown.length > 0 && (
                <>
                  <div style={{
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: 'var(--text-muted)', padding: '2px 0 4px',
                  }}>
                    Selected{pinnedRepos.length !== f.repository_ids.length
                      ? ` (${pinnedRepos.length.toLocaleString()} of `
                        + `${f.repository_ids.length.toLocaleString()} matching this search)`
                      : ''}
                  </div>
                  {pinnedShown.map(r => (
                    <label key={`sel-${r.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0' }}>
                      <input type="checkbox" checked onChange={() => toggleRepo(r)} />
                      <span style={{ color: 'var(--text-muted)' }}>{r.provider}:</span> {r.repo_name}
                    </label>
                  ))}
                  {pinnedRepos.length > MAX_PINNED && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '3px 0' }}>
                      +{(pinnedRepos.length - MAX_PINNED).toLocaleString()} more selected — search to bring one up.
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0' }} />
                </>
              )}
              {pageRepos.map(r => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0' }}>
                  <input type="checkbox" checked={f.repository_ids.includes(r.id)} onChange={() => toggleRepo(r)} />
                  <span style={{ color: 'var(--text-muted)' }}>{r.provider}:</span> {r.repo_name}
                </label>
              ))}
              {repos.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  {reposLoading
                    ? 'Searching…'
                    : (repoSearch.trim() || repoProvider)
                      ? `No ${pinnedShown.length > 0 ? 'other ' : ''}repositories match that search.`
                      : 'No synced repositories. Run the Repository Sync job first.'}
                </div>
              )}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)',
            }}>
              <span>
                {repoTotal === 0
                  ? 'No results'
                  : `${(repoPage - 1) * REPO_PAGE + 1}–${Math.min(repoPage * REPO_PAGE, repoTotal)} `
                    + `of ${repoTotal.toLocaleString()} repositor${repoTotal !== 1 ? 'ies' : 'y'}`}
              </span>
              {repoPages > 1 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px' }}
                    disabled={repoPage <= 1 || reposLoading}
                    onClick={() => setRepoPage(repoPage - 1)}>‹</button>
                  <span style={{ minWidth: 80, textAlign: 'center' }}>Page {repoPage} of {repoPages}</span>
                  <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px' }}
                    disabled={repoPage >= repoPages || reposLoading}
                    onClick={() => setRepoPage(repoPage + 1)}>›</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }}
                disabled={repoTotal === 0 || selectingAll || reposLoading}
                onClick={selectAllMatching}>
                {selectingAll
                  ? 'Selecting…'
                  : `Select all ${repoTotal.toLocaleString()} matching`}
              </button>
              {f.repository_ids.length > 0 && (
                <button type="button" className="btn btn-ghost" onClick={clearSelection}>
                  Clear selection
                </button>
              )}
            </div>
            <p className="form-hint">Pick one or more repos — the same branches, rules and limits apply to all of them. Selected repos are listed first, so you never have to page through the rest to see them. “Select all matching” covers every page of the current search, not just the one on screen. Selections are kept as you search and page. Provider tokens are configured under Repositories → Providers.</p>
            {f.repository_ids.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {f.repository_ids.slice(0, MAX_CHIPS).map(id => {
                  const r = selectedRepos[id];
                  return <span key={id} className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                    {r ? r.repo_name : `#${id}`} <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => removeRepo(id)}>×</span>
                  </span>;
                })}
                {f.repository_ids.length > MAX_CHIPS && (
                  <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                    +{(f.repository_ids.length - MAX_CHIPS).toLocaleString()} more
                  </span>
                )}
              </div>
            )}
            </>)}
          </div>
          <div className="form-group">
            <label className="form-label">Target branches (one per line; globs allowed, e.g. release/*)</label>
            <textarea className="form-control" rows={3} value={f.target_branches} onChange={e => set('target_branches', e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Schedule (cron, UTC)</label>
            <input className="form-control" value={f.cron_expression} onChange={e => set('cron_expression', e.target.value)} placeholder="0 3 * * *" style={{ maxWidth: 220 }} />
            <p className="form-hint">Default: daily at 03:00 UTC. You can also run on demand from the Configs page.</p>
          </div>
        </section>

        <section className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Rules</h3>
          <div className="form-group">
            <label className="form-label">Standard rule packs</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {packs.map(p => (
                <label key={p.pack_key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={f.standard_packs.includes(p.pack_key)} onChange={() => togglePack(p.pack_key)} />
                  {p.name}
                </label>
              ))}
            </div>
            <p className="form-hint">Custom project rules are managed under the Rules tab.</p>
          </div>
        </section>

        <section className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Limits & filters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">PRs per run</label>
              <input className="form-control" value="No limit — daily cost cap governs" disabled
                style={{ color: 'var(--text-muted)' }} />
              <p className="form-hint">Reviews are limited only by the global daily cost cap (set it under Cost &amp; Usage). Scheduled runs also only pick PRs updated since the last review.</p>
            </div>
            <div className="form-group">
              <label className="form-label">Max comments per PR</label>
              <input type="number" min="1" className="form-control" value={f.max_comments_per_pr} onChange={e => set('max_comments_per_pr', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Max files reviewed per PR</label>
              <input type="number" min="1" className="form-control" value={f.max_files_per_pr} onChange={e => set('max_files_per_pr', e.target.value)} />
              <p className="form-hint">Cost guardrail. Files beyond this are reported as not reviewed.</p>
            </div>
            <div className="form-group">
              <label className="form-label">Min severity</label>
              <select className="form-control" value={f.min_severity} onChange={e => set('min_severity', e.target.value)}>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Min confidence (0–1)</label>
              <input type="number" step="0.05" min="0" max="1" className="form-control" value={f.min_confidence} onChange={e => set('min_confidence', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Include globs (optional, one per line)</label>
              <textarea className="form-control" rows={2} value={f.include_globs} onChange={e => set('include_globs', e.target.value)} placeholder="src/**" />
            </div>
            <div className="form-group">
              <label className="form-label">Exclude globs (optional)</label>
              <textarea className="form-control" rows={2} value={f.exclude_globs} onChange={e => set('exclude_globs', e.target.value)} placeholder="**/*.lock&#10;**/vendor/**" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Exclude PRs by title (regex, optional)</label>
            <input className="form-control" value={f.exclude_title_regex}
              onChange={e => set('exclude_title_regex', e.target.value)}
              placeholder="e.g. ^(WIP|DRAFT|\[skip review\])" />
            <p className="form-hint">PRs whose title matches this regular expression are skipped entirely (case-sensitive, matched anywhere in the title). Leave blank to review all titles.</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={f.skip_drafts} onChange={e => set('skip_drafts', e.target.checked)} /> Skip draft PRs
          </label>
        </section>

        <section className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Delivery & model</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Post mode</label>
              <select className="form-control" value={f.post_mode} onChange={e => set('post_mode', e.target.value)}>
                {POST_MODES.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
              {f.post_mode === 'inline' && hasGiteaRepo && (
                <p className="form-hint">
                  This config includes a Gitea repository. Gitea only gained a pull-review
                  API in 1.12 — on older instances the run posts a summary comment listing
                  every finding instead, and says so in the run log.
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Model</label>
              <select className="form-control" value={f.llm_model} onChange={e => set('llm_model', e.target.value)}>
                {modelOptions.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.label}{m.recommended ? ' — recommended' : ''}
                    {m.available === false ? ' (no longer served)' : ''}
                  </option>
                ))}
              </select>
              {selectedModel?.available === false ? (
                <p className="form-hint">
                  The API no longer serves <code>{f.llm_model}</code>. Runs fall back to{' '}
                  <code>{FALLBACK_MODEL}</code> automatically — pick a listed model to make that explicit.
                </p>
              ) : (
                <p className="form-hint">
                  Pricing per model is set under Cost &amp; Usage.
                  {modelsLive ? ' This list is checked against Anthropic\u2019s live model list.' : ''}
                </p>
              )}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={f.enabled} onChange={e => set('enabled', e.target.checked)} /> Enabled (scheduled scans run)
          </label>
        </section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/code-review')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save changes' : 'Create config')}</button>
        </div>
      </form>
    </div>
  );
}
